import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { countriesApi } from '../api/countries.api';
import type { CountryPaginationParams, TerritorialLevel } from '../types/countries.types';
import type { Country } from '../types/countries.types';
import { countryKeys } from './queryKeys';
import type {
  PaginatedResponse,
  ItemRenderer,
} from '@/shared/components/ahkinia/PaginatedSelect/paginated-select.types';

export const useTerritoriesByLevel = (initialLevel?: TerritorialLevel) => {
  const { data, isLoading } = useQuery({
    queryKey: countryKeys.territoryList(initialLevel),
    queryFn: () =>
      countriesApi.getCountries({
        page: 1,
        pageSize: 100,
        level: initialLevel,
      }),
    enabled: !!initialLevel,
  });

  const fetchTerritories = useCallback(
    async (params: {
      page: number;
      pageSize: number;
      search?: string;
    }): Promise<PaginatedResponse<Country>> => {
      const pagination: CountryPaginationParams = {
        page: params.page,
        pageSize: params.pageSize,
        searchTerm: params.search,
        level: initialLevel,
      };

      const response = await countriesApi.getCountries(pagination);
      const data = response.data;

      return {
        items: data.items,
        page: data.page,
        pageSize: data.pageSize,
        totalCount: data.totalCount,
        totalPages: data.totalPages,
        hasPreviousPage: data.hasPreviousPage,
        hasNextPage: data.hasNextPage,
        firstItemOnPage: data.firstItemOnPage,
        lastItemOnPage: data.lastItemOnPage,
      };
    },
    [initialLevel],
  );

  const renderItem = useCallback((item: Country): ItemRenderer => {
    return {
      value: item.id,
      label: item.officialName,
      disabled: !item.isActive,
    };
  }, []);

  return useMemo(
    () => ({
      territories: data?.data?.items ?? [],
      isLoading,
      fetchFn: fetchTerritories,
      renderItem,
    }),
    [data, isLoading, fetchTerritories, renderItem],
  );
};
